import { NgOptimizedImage } from '@angular/common';
import { Component, computed, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DialogComponent } from '@app/shared/dialog/dialog-component.directive';
import { FilterableEntry } from '@app/shared/models/filterable-entry';
import { SharedModule } from '@app/shared/shared.module';
import { componentState } from '@app/utils/component-state';
import { MetaDataProperty, multiOrProperty } from '@app/utils/lists/meta-list';
import { pagedList } from '@app/utils/lists/paged-list';
import { currentLanguage } from '@app/utils/translate';
import { MyNotificationItemHeaderComponent } from '@business/notification/components/my-notification-item-header/my-notification-item-header.component';
import { NotificationLiveViewComponent } from '@business/notification/components/notification-live-view/notification-live-view.component';
import { MyPortalNotificationViewModel } from '@business/notification/models/my-portal-notifications-view-model';
import { PortalNotificationService } from '@business/notification/portal-notification.service';

@Component({
  selector: 'portal-my-notifications-dialog',
  standalone: true,
  imports: [SharedModule, NgOptimizedImage, NotificationLiveViewComponent, MyNotificationItemHeaderComponent],
  templateUrl: './my-notifications-dialog.component.html',
  styles: ``,
  host: {
    class: 'my-notifications-dialog',
  },
})
export class MyNotificationsDialogComponent extends DialogComponent<void, void> {
  error = toSignal(this.portalNotificationService.myNotifications.error$);
  loading = toSignal(this.portalNotificationService.myNotifications.loading$);
  viewModel = toSignal(this.portalNotificationService.myNotifications.data$);

  allItems = computed(() => this.viewModel()?.notifications ?? []);
  items = signal<MyPortalNotificationViewModel[]>([]);
  language = currentLanguage();
  pagedList = pagedList(this.allItems, { sort: ['publishedAt', 'desc'] });
  selected = signal<MyPortalNotificationViewModel | null>(null);
  state = componentState();

  metaDataProperties: MetaDataProperty[] = [
    multiOrProperty('seen', 'common.seen', ['common.seen', 'common.unseen']),
    multiOrProperty('type', 'notification.type', [
      'notification.type.GENERAL',
      'notification.type.MAINTENANCE',
      'notification.type.RELEASE_NOTES',
    ]),
  ];

  sortProperties = {
    date: 'myNotifications.publication',
    name: 'general.name',
  };

  constructor(private portalNotificationService: PortalNotificationService) {
    super();
  }

  calculateMetaData(notification: MyPortalNotificationViewModel): FilterableEntry {
    return {
      key: notification.name,
      meta: {
        seen: notification.seen ? 'common.seen' : 'common.unseen',
        type: `notification.type.${notification.type}`,
      },
    };
  }

  deleteNotification(notificationId: number): void {
    this.state
      .handle(this.portalNotificationService.deleteMyNotification(notificationId), `delete:${notificationId}`)
      .subscribe();
  }

  markAsSeen(notification: MyPortalNotificationViewModel): void {
    if (notification.seen === false) {
      this.state
        .handle(this.portalNotificationService.markAsSeen(notification.id), `mark-seen:${notification.id}`)
        .subscribe();
    }
  }

  setItems(items: MyPortalNotificationViewModel[]) {
    this.pagedList.updateFilteredEntries(items);
  }

  toggle(notification: MyPortalNotificationViewModel): void {
    if (this.selected()?.id === notification.id) {
      this.selected.set(null);
    } else {
      this.selected.set(notification);
    }
  }
}
